import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import {
  documentStatusLabel,
  documentStatusTone,
  requiredActionLabel,
} from '@/modules/agreements/shared';
import { useDocumentDetail } from './hooks/use-document-detail';
import { VersionsSection } from './parts/versions-section';
import { RequirementsSection } from './parts/requirements-section';
import type { AgreementDocumentResponse } from '@/api/schemas/agreements-platform';

function documentBadges(document: AgreementDocumentResponse): ReactNode[] {
  return [
    <Badge key="audience" tone="info">
      {document.audience}
    </Badge>,
    <Badge key="action" tone="neutral">
      {requiredActionLabel(document.requiredAction)}
    </Badge>,
  ];
}

/**
 * One agreement document: its identity, its published versions, and the requirements that
 * bind subjects to a version.
 *
 * A requirement is configured against a SPECIFIC version, so the requirement form appears
 * once a version exists.
 */
export function DocumentDetailPage(props: { documentId: string }): ReactNode {
  const detail = useDocumentDetail(props.documentId);
  const document = detail.document;

  return (
    <AppDetailLayout
      headerVariant="accent"
      title={document?.title ?? 'Agreement document'}
      subtitle={document ? `Key: ${document.documentKey}` : undefined}
      breadcrumbs={[
        { label: 'Platform', href: '/platform/tenants' },
        { label: 'Agreements', href: '/platform/agreements/documents' },
        { label: document?.title ?? 'Document' },
      ]}
      isLoading={detail.isLoading}
      isDenied={detail.isDenied}
      error={detail.error}
      status={
        document
          ? {
              label: documentStatusLabel(document.status),
              tone: documentStatusTone(document.status),
            }
          : undefined
      }
      badges={document ? documentBadges(document) : undefined}
      tabs={[
        { id: 'versions', label: 'Versions' },
        { id: 'requirements', label: 'Requirements' },
      ]}
      tabsInHeader
      sections={[
        {
          id: 'versions',
          label: 'Versions',
          content: (
            <VersionsSection
              documentId={props.documentId}
              selectedVersionId={detail.selectedVersionId}
              onChanged={detail.reload}
              onSelectVersion={detail.setSelectedVersionId}
            />
          ),
        },
        {
          id: 'requirements',
          label: 'Requirements',
          content: document ? (
            <RequirementsSection
              documentId={props.documentId}
              audience={document.audience === 'both' ? 'tenant' : document.audience}
              selectedVersionId={detail.selectedVersionId}
              onChanged={detail.reload}
            />
          ) : null,
        },
      ]}
    />
  );
}
